/**
 * LikerCredibilitySection — band-distribution of liker credibility scores.
 *
 * Phyllo returns audience_likes as an array of {credibility: {min, max,
 * total_profile_count}} entries. Each entry is a band (e.g. 0.30–0.40)
 * showing how many liker profiles fall in that credibility range.
 *
 * Renders one HBar per band, label `30–40%`, value = profile count, bar
 * pct = share-of-total. Reuses the existing HBar component (also used 6x
 * elsewhere in AnalyticsTab for country/city/state/interest bars).
 *
 * Visible to BOTH admin and brand — audience-quality signal that brand
 * wants when sizing creators.
 */
import { useMemo } from 'react';
import type { InsightIQProfile } from '@discovr/types';
import { SectionTitle } from '../charts/StatCard';
import { HBar } from '../charts/HBar';
import { InfoTip } from '../charts/InfoTip';
import { fmtNum } from '../utils/formatters';

const BLUE = '#3b82f6';

interface LikerCredibilitySectionProps {
  bands: NonNullable<InsightIQProfile['audience_likes']>;
}

export function LikerCredibilitySection({ bands }: LikerCredibilitySectionProps) {
  const { rows, total } = useMemo(() => {
    const sorted = [...bands].sort((a, b) => a.credibility.min - b.credibility.min);
    const t = sorted.reduce((sum, b) => sum + b.credibility.total_profile_count, 0);
    if (t === 0) return { rows: [], total: 0 };
    return {
      total: t,
      rows: sorted.map((b) => {
        const lo = (b.credibility.min * 100).toFixed(0);
        const hi = (b.credibility.max * 100).toFixed(0);
        const pct = (b.credibility.total_profile_count / t) * 100;
        return {
          label: `${lo}–${hi}%`,
          value: fmtNum(b.credibility.total_profile_count),
          pct: Math.round(pct * 10) / 10,
        };
      }),
    };
  }, [bands]);

  if (rows.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <SectionTitle>
        Liker Audience Credibility
        <InfoTip label="What is Liker Audience Credibility?">
          A read on how real the people liking this creator&apos;s posts are. Each liker gets a
          score from 0 to 100% for how likely they are to be a genuine account (not a bot or spam),
          and we group them into bands. The more that sit in the higher bands, the more trustworthy
          the audience.
        </InfoTip>
      </SectionTitle>
      <div className="mb-3 text-caption-regular text-foreground">
        Distribution across {fmtNum(total)} liker profiles
      </div>
      {rows.map((row) => (
        <HBar
          key={row.label}
          label={row.label}
          value={row.value}
          pct={row.pct}
          color={BLUE}
        />
      ))}
    </div>
  );
}
